import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import Sortable from "sortablejs";
import type { ColumnConfig } from "./types";
import { ColumnToggleIcon } from "./icons";

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

interface Props {
  columns: ColumnConfig[];
  hiddenColumns: Set<string>;
  onToggle: (columnId: string) => void;
  columnOrder: string[];
  onReorder: (fromIndex: number, toIndex: number) => void;
  onReset: () => void;
}

const MENU_OFFSET = 8;

export default function ColumnToggle({ columns, hiddenColumns, onToggle, columnOrder, onReorder, onReset }: Props) {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top?: number; bottom?: number; left: number }>({ left: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const onReorderRef = useRef(onReorder);
  onReorderRef.current = onReorder;

  const [localHidden, setLocalHidden] = useState(hiddenColumns);
  useEffect(() => { setLocalHidden(hiddenColumns); }, [hiddenColumns]);

  const dataColumns = useMemo(() =>
    columnOrder
      .map((id) => columns.find((c) => c.id === id))
      .filter((c): c is ColumnConfig => !!c && c.id !== "__actions__"),
    [columnOrder, columns],
  );

  const lockedColumnId = useMemo(() => {
    const linkCol = dataColumns.find((c) => c.is_link);
    return linkCol?.id ?? dataColumns[0]?.id;
  }, [dataColumns]);

  const handleToggle = useCallback((colId: string) => {
    if (colId === lockedColumnId) return;
    setLocalHidden((prev) => {
      const next = new Set(prev);
      if (next.has(colId)) next.delete(colId);
      else next.add(colId);
      return next;
    });
    onToggle(colId);
  }, [onToggle, lockedColumnId]);

  const updatePosition = useCallback(() => {
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    if (window.innerHeight - rect.bottom >= rect.top) {
      setMenuPos({ top: rect.bottom + MENU_OFFSET, bottom: undefined, left: rect.right });
    } else {
      setMenuPos({ bottom: window.innerHeight - rect.top + MENU_OFFSET, top: undefined, left: rect.right });
    }
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        wrapRef.current && !wrapRef.current.contains(target) &&
        menuRef.current && !menuRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Reposition on scroll/resize
  useEffect(() => {
    if (!open) return;
    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open, updatePosition]);

  // SortableJS
  useEffect(() => {
    if (!open || !listRef.current) return;
    const instance = Sortable.create(listRef.current, {
      animation: 150,
      draggable: ".lt-col-toggle__item",
      ghostClass: "lt-col-toggle__item--ghost",
      chosenClass: "lt-col-toggle__item--chosen",
      onEnd: ({ item, from, oldIndex, newIndex }) => {
        if (oldIndex != null) {
          from.insertBefore(item, from.children[oldIndex] || null);
        }
        if (oldIndex != null && newIndex != null && oldIndex !== newIndex) {
          onReorderRef.current(oldIndex, newIndex);
        }
      },
    });
    return () => { instance.destroy(); };
  }, [open]);

  return (
    <div className="lt-col-toggle" ref={wrapRef}>
      <button
        ref={btnRef}
        type="button"
        className="lt-header-menu-btn"
        onClick={() => setOpen(!open)}
        title="Настроить колонки"
      >
        <ColumnToggleIcon />
      </button>
      {open && createPortal(
        <div
          ref={menuRef}
          className="lt-col-toggle__menu"
          style={{
            position: "fixed",
            top: menuPos.top,
            bottom: menuPos.bottom,
            right: document.documentElement.clientWidth - menuPos.left,
            zIndex: 1030,
          }}
        >
          <div className="lt-col-toggle__header">
            <div className="lt-col-toggle__title">Колонки</div>
            <button
              type="button"
              className="lt-col-toggle__reset"
              onClick={onReset}
              title="Сбросить порядок колонок"
            >
              <i className="fas fa-undo" />
            </button>
          </div>
          <div ref={listRef} className="lt-col-toggle__list">
            {dataColumns.map((col) => (
              <div key={col.id} className="lt-col-toggle__item">
                <span className="lt-col-toggle__drag">⠿</span>
                <label className="lt-col-toggle__label">
                  {col.id === lockedColumnId ? (
                    <i className="far fa-do-not-enter lt-checkbox--disabled" />
                  ) : (
                    <input
                      type="checkbox"
                      className="lt-checkbox"
                      checked={!localHidden.has(col.id)}
                      onChange={() => handleToggle(col.id)}
                    />
                  )}
                  <span className="lt-col-toggle__text">{capitalize(col.header)}</span>
                </label>
              </div>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
